import type { ReactNode } from 'react'

import { CHART_HEAD } from '@/components/charts/chart-header-layout'
import { cn } from '@/lib/utils'

interface ChartHeaderProps {
  /** The caller's own heading: Overview's day headline, Indices' index name. */
  title?: ReactNode
  /** The value row under it: the level, its move and, where the chart has one, the status pill. */
  figure?: ReactNode
  /** `ChartTypeToggle`, with no `action` of its own: the chart button is `action` here. */
  toggle?: ReactNode
  /** The 40px chart button after the toggle (`TechChartLink`). */
  action?: ReactNode
  /** `PeriodTabs variant="boxed"`. The only place a chart's `PeriodTabs` may sit (lint-enforced). */
  periods?: ReactNode
  /**
   * A state-only line (UpdatingNote) that LEADS the toggle row, so it grows leftward into the
   * free space rather than sliding the toggle the member just clicked out from under the pointer.
   * With no toggle or button it leads the periods instead.
   */
  note?: ReactNode
  className?: string | undefined
}

/**
 * Every Market chart's head, to the owner's ruling 3 (2026-10-28): what the chart says on the
 * left, what it can do on the right — the mock's band with its period tabs moved up under Line /
 * Candles. See `CHART_HEAD` for the boxes and the narrow-container fallback. The chart itself
 * goes 24 under it (`CHART_UNDER_HEAD`).
 *
 * Only the layout: each slot is the caller's own element, so the Overview and Indices bands keep
 * their own headings, figures and range state.
 *
 * Every slot is optional, so every chart in the app takes this head, not only Market's: a chart
 * with no toggle or button sets its periods top-right on their own (`CHART_HEAD.bare`), and one
 * whose title sits outside it (a card's state wrapper) passes the figure alone.
 */
export default function ChartHeader({
  title,
  figure,
  toggle,
  action,
  periods,
  note,
  className,
}: ChartHeaderProps) {
  const hasControls = toggle != null || action != null
  const hasActions = hasControls || periods != null || note != null
  return (
    <div className={cn(CHART_HEAD.root, className)}>
      <div className={CHART_HEAD.grid}>
        <div className={CHART_HEAD.info}>
          {title}
          {figure}
        </div>
        {hasActions ? (
          <div className={CHART_HEAD.actions}>
            {hasControls ? (
              <>
                <div className={CHART_HEAD.controls}>
                  {toggle}
                  {/* After the toggle in the DOM, so focus meets the controls first; drawn first,
                      and pushed out to the left, from `@xl` up. Below it sits between toggle and
                      button, pushing the button to the far right. */}
                  {note === undefined ? null : (
                    <div className="ml-auto flex min-w-0 justify-end @xl/chart-head:order-first">
                      {note}
                    </div>
                  )}
                  {action == null ? null : (
                    <div
                      className={cn(
                        'shrink-0',
                        note === undefined && 'ml-auto @xl/chart-head:ml-0',
                      )}
                    >
                      {action}
                    </div>
                  )}
                </div>
                {periods == null ? null : <div className={CHART_HEAD.periods}>{periods}</div>}
              </>
            ) : (
              <div className={CHART_HEAD.bare}>
                {note}
                {periods == null ? null : <div className={CHART_HEAD.periods}>{periods}</div>}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}
